<script setup lang="ts">
/**
 * The navigation icons, as line art rather than emoji.
 *
 * Emoji render differently on every platform, carry their own colour, and are announced by
 * a screen reader as whatever the vendor named them — "card index dividers" for a chart.
 * A stroked path inherits `currentColor`, so the rail can tint the active one brass, and
 * it stays `aria-hidden` because the label beside it already says what it is.
 */
export type NavIconName =
  | 'home'
  | 'news'
  | 'gift'
  | 'calendar'
  | 'people'
  | 'org'
  | 'doc'
  | 'learn'
  | 'brand'
  | 'money'
  | 'person'
  | 'help'
  // A tick inside a circle: work that is waiting for somebody to resolve it.
  | 'check';

defineProps<{ name: NavIconName; size?: number }>();
</script>

<template>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
    stroke-linejoin="round"
    :width="size ?? 16"
    :height="size ?? 16"
    aria-hidden="true"
    class="shrink-0"
  >
    <template v-if="name === 'home'">
      <path d="M3 12l9-8 9 8M5 10v10h14V10" />
    </template>
    <template v-else-if="name === 'news'">
      <path d="M4 5h13v14H4zM17 8h3v11H6" />
      <path d="M7 9h7M7 12h7M7 15h4" />
    </template>
    <template v-else-if="name === 'gift'">
      <path d="M20 12v9H4v-9M2 7h20v5H2zM12 7v14" />
      <path d="M12 7c-2-4-7-4-7-1s5 1 7 1zM12 7c2-4 7-4 7-1s-5 1-7 1z" />
    </template>
    <template v-else-if="name === 'calendar'">
      <rect
        x="3"
        y="5"
        width="18"
        height="16"
        rx="2"
      />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </template>
    <template v-else-if="name === 'people'">
      <circle
        cx="9"
        cy="8"
        r="3.2"
      />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 4a3.2 3.2 0 010 8M21 20c0-2.6-1.6-4.8-4-5.6" />
    </template>
    <template v-else-if="name === 'org'">
      <circle
        cx="12"
        cy="5"
        r="2.5"
      />
      <circle
        cx="5"
        cy="18"
        r="2.5"
      />
      <circle
        cx="19"
        cy="18"
        r="2.5"
      />
      <path d="M12 8v4M12 12l-6 3.5M12 12l6 3.5" />
    </template>
    <template v-else-if="name === 'doc'">
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4M9 12h6M9 16h6" />
    </template>
    <template v-else-if="name === 'learn'">
      <path d="M12 4L2 9l10 5 10-5-10-5zM6 11.5V16c0 1.7 2.7 3 6 3s6-1.3 6-3v-4.5" />
    </template>
    <template v-else-if="name === 'check'">
      <circle
        cx="12"
        cy="12"
        r="9"
      />
      <path d="M8.5 12.3l2.4 2.4 4.6-4.9" />
    </template>
    <template v-else-if="name === 'help'">
      <circle
        cx="12"
        cy="12"
        r="9"
      />
      <path d="M9.5 9.5a2.5 2.5 0 113.2 2.4c-.5.2-.7.6-.7 1.1v.5" />
      <path d="M12 16.5h.01" />
    </template>
    <template v-else-if="name === 'person'">
      <circle
        cx="12"
        cy="8"
        r="3.5"
      />
      <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
    </template>
    <template v-else-if="name === 'money'">
      <rect
        x="2.5"
        y="6"
        width="19"
        height="12"
        rx="2"
      />
      <circle
        cx="12"
        cy="12"
        r="2.75"
      />
      <path d="M6 9.5v5M18 9.5v5" />
    </template>
    <template v-else-if="name === 'brand'">
      <path d="M12 3a9 9 0 000 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.2 0-.8.7-1.5 1.5-1.5H16a5 5 0 005-5c0-3.9-4-7-9-7z" />
      <circle
        cx="7.5"
        cy="11.5"
        r="1.2"
      />
      <circle
        cx="11"
        cy="7.5"
        r="1.2"
      />
      <circle
        cx="15.5"
        cy="9"
        r="1.2"
      />
    </template>
  </svg>
</template>
